import {
  CONTRIBUTION_KINDS,
  type ContributionEntry,
  type ContributionHistory,
  type ContributionKind,
} from '@mfos/shared';
import { type SubmitEvent, useState } from 'react';
import { Link } from 'react-router';
import { Badge } from '../../components/Badge';
import { ErrorNotice, LoadingNotice } from '../../components/ErrorNotice';
import { SelectField, TextField } from '../../components/Field';
import { formatDate, formatDateTime, humanize } from '../../lib/format';
import { useContributions, type ContributionFilters } from './reports.api';
import { phrase } from '../../lib/i18n/runtime';

const KIND_LABELS: Readonly<Record<ContributionKind, string>> = {
  TASK_COMPLETED: 'Task completed',
  UPDATE_SUBMITTED: 'Update submitted',
  OBLIGATION_PERIOD: 'Responsibility period',
  DECISION_OWNED: 'Decision owned',
  BLOCKER_RESOLVED: 'Blocker resolved',
};

/**
 * Founder contribution history (spec §7.8): a timeline of qualitative evidence grouped by week, and
 * committed-vs-reported per responsibility. Deliberately no totals, scores or comparisons.
 */
export function ContributionsSection({
  workspaceId,
  founderId,
  timeZone,
}: {
  workspaceId: string;
  founderId: string;
  timeZone: string | undefined;
}) {
  const [filters, setFilters] = useState<ContributionFilters>({});
  const history = useContributions(workspaceId, founderId, filters);

  return (
    <section className="card stack" aria-labelledby="contributions-heading">
      <h2 id="contributions-heading">{phrase('Contribution history')}</h2>
      <p className="meta">
        {phrase(
          'Evidence of work: completed tasks, submitted updates, responsibility periods, decisions and resolved blockers. There is no score — read the items themselves.',
        )}
      </p>
      <ContributionFiltersForm onApply={setFilters} />
      {history.isPending && (
        <LoadingNotice>{phrase('Loading contribution history…')}</LoadingNotice>
      )}
      {history.isError && <ErrorNotice error={history.error} />}
      {history.isSuccess && (
        <ContributionView history={history.data} workspaceId={workspaceId} timeZone={timeZone} />
      )}
    </section>
  );
}

function ContributionFiltersForm({ onApply }: { onApply: (filters: ContributionFilters) => void }) {
  const [kind, setKind] = useState('');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [error, setError] = useState<{ field: 'q' | 'from'; message: string } | null>(null);

  function submit(event: SubmitEvent) {
    event.preventDefault();
    const search = q.trim();
    if (search.length === 1) {
      setError({ field: 'q', message: 'Enter at least 2 characters.' });
      return;
    }
    if (from !== '' && to !== '' && from > to) {
      setError({ field: 'from', message: '“From” must not be after “To”.' });
      return;
    }
    setError(null);
    const parsedKind = CONTRIBUTION_KINDS.find((value) => value === kind);
    onApply({
      ...(parsedKind !== undefined ? { kind: parsedKind } : {}),
      ...(search !== '' ? { q: search } : {}),
      ...(from !== '' ? { from } : {}),
      ...(to !== '' ? { to } : {}),
    });
  }

  return (
    <form
      className="inline-form"
      onSubmit={submit}
      noValidate
      aria-label={phrase('Filter contributions')}
    >
      <SelectField
        label={phrase('Kind')}
        value={kind}
        onValueChange={setKind}
        options={CONTRIBUTION_KINDS.map((value) => ({ value, label: KIND_LABELS[value] }))}
        placeholder={phrase('All kinds')}
      />
      <TextField
        label={phrase('Search')}
        type="search"
        value={q}
        onValueChange={setQ}
        error={error?.field === 'q' ? error.message : undefined}
        maxLength={200}
      />
      <TextField
        label={phrase('From')}
        type="date"
        value={from}
        onValueChange={setFrom}
        error={error?.field === 'from' ? error.message : undefined}
      />
      <TextField label={phrase('To')} type="date" value={to} onValueChange={setTo} />
      <button type="submit" className="button button--secondary">
        {phrase('Apply filters')}
      </button>
    </form>
  );
}

function groupByWeek(entries: readonly ContributionEntry[]): [string, ContributionEntry[]][] {
  const weeks = new Map<string, ContributionEntry[]>();
  for (const entry of entries) {
    const list = weeks.get(entry.weekStart) ?? [];
    list.push(entry);
    weeks.set(entry.weekStart, list);
  }
  return [...weeks.entries()];
}

function ContributionView({
  history,
  workspaceId,
  timeZone,
}: {
  history: ContributionHistory;
  workspaceId: string;
  timeZone: string | undefined;
}) {
  const base = `/w/${workspaceId}`;
  return (
    <>
      <p className="meta">
        {formatDate(history.from)} – {formatDate(history.to)} (weeks in {history.timezone})
      </p>
      {history.commitments !== null && history.commitments.length > 0 && (
        <div className="stack">
          <h3>{phrase('Committed vs reported')}</h3>
          <div
            className="table-scroll"
            role="region"
            aria-label={phrase('Committed vs reported')}
            tabIndex={0}
          >
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{phrase('Responsibility')}</th>
                  <th scope="col">{phrase('Periods')}</th>
                  <th scope="col">{phrase('Completed')}</th>
                  <th scope="col">{phrase('Missed')}</th>
                  <th scope="col">{phrase('Excused')}</th>
                  <th scope="col">{phrase('Still open')}</th>
                  <th scope="col">{phrase('Mentioned in updates')}</th>
                </tr>
              </thead>
              <tbody>
                {history.commitments.map((item) => (
                  <tr key={item.obligationId}>
                    <th scope="row">
                      {item.commitment} <span className="meta">· {humanize(item.category)}</span>
                    </th>
                    <td className="numeric">{item.periods}</td>
                    <td className="numeric">{item.completed}</td>
                    <td className="numeric">{item.missed}</td>
                    <td className="numeric">{item.excused}</td>
                    <td className="numeric">{item.open}</td>
                    <td className="numeric">{item.reportedInUpdates}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {history.truncated && (
        <p className="notice notice--warn" role="status">
          <span aria-hidden="true">! </span>
          {phrase('Showing the most recent items only. Narrow the dates to see everything.')}
        </p>
      )}
      {history.entries.length === 0 ? (
        <p className="meta">{phrase('No contributions recorded in this window.')}</p>
      ) : (
        groupByWeek(history.entries).map(([week, entries]) => (
          <div key={week} className="stack">
            <h3>Week of {formatDate(week)}</h3>
            <ul className="item-list">
              {entries.map((entry, index) => (
                <li key={`${entry.kind}:${entry.occurredAt}:${String(index)}`} className="item">
                  <div className="badge-row">
                    <Badge tone="neutral" label={KIND_LABELS[entry.kind]} />
                    {entry.outcome !== null && (
                      <Badge tone="info" label={humanize(entry.outcome)} />
                    )}
                    {entry.category !== null && (
                      <span className="meta">{humanize(entry.category)}</span>
                    )}
                  </div>
                  <p className="item__title">
                    {entry.link !== null ? (
                      <Link to={`${base}${entry.link}`}>{entry.title}</Link>
                    ) : (
                      entry.title
                    )}
                  </p>
                  {entry.detail !== null && <p className="prewrap">{entry.detail}</p>}
                  <p className="meta">
                    <time dateTime={entry.occurredAt}>
                      {formatDateTime(entry.occurredAt, timeZone)}
                    </time>
                    {entry.evidenceUrl !== null && (
                      <>
                        {' · '}
                        <a href={entry.evidenceUrl} target="_blank" rel="noopener noreferrer">
                          {phrase('Evidence')}
                          <span className="visually-hidden"> (opens in a new tab)</span>
                        </a>
                      </>
                    )}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </>
  );
}
